import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Alert,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest, getRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers, title }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onCustomerAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                          No customers found
                        </Typography>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function SalePurchaseDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const authCtx = useContext(AuthContext);

  // Get basicFormData from previous step (SimpleConveyancingForm)
  const basicFormData = location.state?.basicFormData || {};
  console.log('📋 Basic Form Data from Step 1:', basicFormData);

  console.log('🚀 SalePurchaseDetails Component Loaded');
  console.log('📍 ID from useParams:', id);
  console.log('🔑 Auth token exists?', !!authCtx.token);

  // Get customerId from URL query params (same as Purchase form)
  const [searchParams] = useSearchParams();
  // Support both 'customerId' and 'customer_id' parameter names
  const customerIdFromUrl = searchParams.get('customerId') || searchParams.get('customer_id');
  console.log('👤 Customer ID from URL:', customerIdFromUrl);

  const [conveyancingData, setConveyancingData] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [showSellerModal, setShowSellerModal] = useState(false);
  const [showPurchaserModal, setShowPurchaserModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);

  // Sale postcode branch validation states
  const [salePostcodeMatch, setSalePostcodeMatch] = useState(null);
  const [availableSaleBranches, setAvailableSaleBranches] = useState([]);
  const [selectedSaleBranch, setSelectedSaleBranch] = useState(null);
  const [checkingSalePostcode, setCheckingSalePostcode] = useState(false);
  const [isSalePostcodeValid, setIsSalePostcodeValid] = useState(false); // Track if sale postcode is approved

  // Purchase postcode branch validation states
  const [purchasePostcodeMatch, setPurchasePostcodeMatch] = useState(null);
  const [availablePurchaseBranches, setAvailablePurchaseBranches] = useState([]);
  const [selectedPurchaseBranch, setSelectedPurchaseBranch] = useState(null);
  const [checkingPurchasePostcode, setCheckingPurchasePostcode] = useState(false);
  const [isPurchasePostcodeValid, setIsPurchasePostcodeValid] = useState(false); // Track if purchase postcode is approved

  // Calculate default completion date (today + 3 months)
  const getDefaultCompletionDate = () => {
    const today = new Date();
    today.setMonth(today.getMonth() + 3);
    return today.toISOString().split('T')[0];
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      // Sale Property Details
      saleLine1: '',
      saleLine2: '',
      saleTown: '',
      saleCounty: '',
      salePostcode: '',
      salePrice: '',
      saleOwnershipType: '',

      // Purchase Property Details
      purchaseLine1: '',
      purchaseLine2: '',
      purchaseTown: '',
      purchaseCounty: '',
      purchasePostcode: '',
      purchasePrice: '',
      purchaseOwnershipType: '',
      purchaseCompletionDate: getDefaultCompletionDate(),

      // Lender Panel
      lender_panel_id: '',
      partner_id: '',

      // Info section
      firstTimeBuyer: false,
      helpToBuyISA: false,
      helpToBuy: false,
      helpToBuyEquityLoan: false,
      rightToBuy: false,
      sharedOwnership: false,
      buyingWithMortgage: false,
      sellingWithMortgage: false,
      arrangingRedemption: false,
      newBuild: false,
      islamicMortgage: false,
      auctionRepossession: false,
      giftedDeposit: '',
      multipleProperties: '',
      buyToLet: false,
      arrangingIndemnityPolicy: false,
      unregisteredProperty: false,
      staircasing: '',
      isNonUKResident: '',
      nonResidentialMixed: false
    }
  });

  // Watch postcode fields
  const salePostcodeValue = watch('salePostcode');
  const purchasePostcodeValue = watch('purchasePostcode');

  // Ownership type options
  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold' },
    { value: 'leasehold', label: 'Leasehold' }
  ];

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch conveyancing details
  const fetchConveyancingDetails = async () => {
    console.log('🔍 === FETCHING SALE & PURCHASE DETAILS ===');
    console.log('📍 Lead ID:', id);

    // Skip fetching for new leads
    if (id === 'new') {
      console.log('⏭️  Skipping fetch - creating new lead');
      return;
    }

    try {
      setLoading(true);

      // First try the dedicated Sale & Purchase endpoint (like SaleDetails and PurchaseDetails do)
      let endpoint = `private/getEditDataSalePurchaseConveyancing/${id}`;
      console.log('🌐 Trying Sale & Purchase specific endpoint:', endpoint);

      let mainLeadResponse;
      try {
        mainLeadResponse = await getRequest(endpoint, authCtx.token);
        console.log('✅ Got response from Sale & Purchase endpoint:', mainLeadResponse);
      } catch (specificError) {
        console.log('⚠️ Sale & Purchase endpoint failed, trying fallback to main lead endpoint...');
        // Fallback to main lead endpoint
        mainLeadResponse = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);
        console.log('✅ Got response from main lead endpoint:', mainLeadResponse);
      }

      if (mainLeadResponse?.status === 200 && mainLeadResponse?.data) {
        const lead = mainLeadResponse.data;
        console.log('✅ Found main lead data:', lead);
        console.log('📋 Lead service type:', lead.service_type);
        console.log('👥 Main lead customers:', lead.customers);
        console.log('🏠 Sale property details exist?', !!lead.salePropertyDetails);
        console.log('🏡 Purchase property details exist?', !!lead.purchasePropertyDetails);

        setConveyancingData(lead);

        // Auto-fill Sellers/Vendors from main lead vendors (or customers as fallback)
        console.log('👥 === AUTO-FILLING SELLERS ===');

        let sellersToSet = [];

        // Use vendors field first, fallback to customers if vendors not available
        const vendorsSource = lead.vendors || lead.customers || [];
        console.log('📊 Vendors source:', vendorsSource.length, 'entries');

        // Check if vendors are already populated (objects) or just IDs
        if (vendorsSource.length > 0) {
          const firstVendor = vendorsSource[0];

          if (typeof firstVendor === 'object' && firstVendor._id) {
            // Already populated
            sellersToSet = vendorsSource;
            console.log('✅ Using populated vendors:', sellersToSet.length);
          } else if (typeof firstVendor === 'string') {
            // Need to fetch
            console.log('🔍 Fetching vendors by IDs:', vendorsSource);
            try {
              const vendorPromises = vendorsSource.map(async (vendorId) => {
                try {
                  const vendorRes = await postRequest(`private/getCustomerIndividualData/${vendorId}`, {}, authCtx.token);
                  return vendorRes?.data || null;
                } catch (err) {
                  console.log('❌ Error fetching vendor', vendorId);
                  return null;
                }
              });
              const vendors = await Promise.all(vendorPromises);
              sellersToSet = vendors.filter(v => v && v._id);
              console.log('✅ Vendors fetched successfully:', sellersToSet.length);
            } catch (err) {
              console.log('❌ Error in vendor fetching:', err);
            }
          }
        }

        // Only set if not already set from URL customerId parameter
        if (sellers.length === 0) {
          setSellers(sellersToSet);
          console.log('✅ Sellers state updated with', sellersToSet.length, 'sellers');
        } else {
          console.log('⚠️ Sellers already set (from URL), not overwriting');
        }

        // Auto-fill Purchasers from main lead purchasers (or customers as fallback)
        console.log('👥 === AUTO-FILLING PURCHASERS ===');

        let purchasersToSet = [];

        // Use purchasers field first, fallback to customers if purchasers not available
        const purchasersSource = lead.purchasers || lead.customers || [];
        console.log('📊 Purchasers source:', purchasersSource.length, 'entries');

        // Check if purchasers are already populated (objects) or just IDs
        if (purchasersSource.length > 0) {
          const firstPurchaser = purchasersSource[0];

          if (typeof firstPurchaser === 'object' && firstPurchaser._id) {
            // Already populated
            purchasersToSet = purchasersSource;
            console.log('✅ Using populated purchasers:', purchasersToSet.length);
          } else if (typeof firstPurchaser === 'string') {
            // Need to fetch
            console.log('🔍 Fetching purchasers by IDs:', purchasersSource);
            try {
              const purchaserPromises = purchasersSource.map(async (purchaserId) => {
                try {
                  const purchaserRes = await postRequest(`private/getCustomerIndividualData/${purchaserId}`, {}, authCtx.token);
                  return purchaserRes?.data || null;
                } catch (err) {
                  console.log('❌ Error fetching purchaser', purchaserId);
                  return null;
                }
              });
              const purchasers = await Promise.all(purchaserPromises);
              purchasersToSet = purchasers.filter(p => p && p._id);
              console.log('✅ Purchasers fetched successfully:', purchasersToSet.length);
            } catch (err) {
              console.log('❌ Error in purchaser fetching:', err);
            }
          }
        }

        // Only set if not already set from URL customerId parameter
        if (purchasers.length === 0) {
          setPurchasers(purchasersToSet);
          console.log('✅ Purchasers state updated with', purchasersToSet.length, 'purchasers');
        } else {
          console.log('⚠️ Purchasers already set (from URL), not overwriting');
        }

        // Prepare all form values to reset at once - Start with default values
        const formValues = {
          // Sale defaults
          saleLine1: '',
          saleLine2: '',
          saleTown: '',
          saleCounty: '',
          salePostcode: '',
          salePrice: '',
          saleOwnershipType: '',
          // Purchase defaults
          purchaseLine1: '',
          purchaseLine2: '',
          purchaseTown: '',
          purchaseCounty: '',
          purchasePostcode: '',
          purchasePrice: '',
          purchaseOwnershipType: '',
          purchaseCompletionDate: getDefaultCompletionDate(),
          // Sale Info defaults
          sellingWithMortgage: false,
          arrangingIndemnityPolicy: false,
          unregisteredProperty: false,
          sharedOwnership: false,
          auctionRepossession: false,
          // Purchase Info defaults
          firstTimeBuyer: false,
          helpToBuyISA: false,
          helpToBuy: false,
          helpToBuyEquityLoan: false,
          rightToBuy: false,
          islamicMortgage: false,
          buyToLet: false,
          isNonUKResident: '',
          buyingWithMortgage: false,
          arrangingRedemption: false,
          newBuild: false,
          giftedDeposit: '',
          multipleProperties: '',
          staircasing: '',
          nonResidentialMixed: false
        };

        console.log('📦 === ENTIRE LEAD DATA ===');
        console.log('Full lead object:', lead);
        console.log('lead.salePropertyDetails:', lead.salePropertyDetails);
        console.log('lead.purchasePropertyDetails:', lead.purchasePropertyDetails);

        // Populate Sale Property Details
        console.log('🏠 === POPULATING SALE DETAILS ===');
        if (lead.salePropertyDetails) {
          const saleData = lead.salePropertyDetails;
          console.log('📊 Sale data:', saleData);
          console.log('Sale address:', saleData.address);

          if (saleData.address) {
            console.log('📮 Sale address:', saleData.address);
            formValues.saleLine1 = saleData.address.line1 || '';
            formValues.saleLine2 = saleData.address.line2 || '';
            formValues.saleTown = saleData.address.town || '';
            formValues.saleCounty = saleData.address.county || '';
            formValues.salePostcode = saleData.address.postcode || '';
          }
          formValues.salePrice = saleData.salePrice || '';
          formValues.saleOwnershipType = saleData.ownershipType || '';
          console.log('✅ Sale details prepared');
        } else {
          console.log('⚠️ No sale property details found');
        }

        // Populate Purchase Property Details
        console.log('🏡 === POPULATING PURCHASE DETAILS ===');
        if (lead.purchasePropertyDetails) {
          const purchaseData = lead.purchasePropertyDetails;
          console.log('📊 Purchase data:', purchaseData);

          if (purchaseData.address) {
            console.log('📮 Purchase address:', purchaseData.address);
            formValues.purchaseLine1 = purchaseData.address.line1 || '';
            formValues.purchaseLine2 = purchaseData.address.line2 || '';
            formValues.purchaseTown = purchaseData.address.town || '';
            formValues.purchaseCounty = purchaseData.address.county || '';
            formValues.purchasePostcode = purchaseData.address.postcode || '';
          }
          formValues.purchasePrice = purchaseData.purchasePrice || '';
          formValues.purchaseOwnershipType = purchaseData.ownershipType || '';

          // Handle completion date
          if (purchaseData.completionDate) {
            try {
              const date = new Date(purchaseData.completionDate);
              const formattedDate = date.toISOString().split('T')[0];
              console.log('📅 Setting completion date:', formattedDate);
              formValues.purchaseCompletionDate = formattedDate;
            } catch (err) {
              console.log('Date parsing error:', err);
            }
          }
          console.log('✅ Purchase details prepared');
        } else {
          console.log('⚠️ No purchase property details found');
        }

        // Populate Sale Info section checkboxes
        console.log('☑️ === POPULATING SALE INFO CHECKBOXES ===');
        // Check top-level info first (new structure), then nested (backward compatibility)
        const infoData = lead.info ||
                         (lead.salePropertyDetails && lead.salePropertyDetails.info) ||
                         {};
        console.log('📊 Info data:', infoData);

        // Sale-specific info fields (5 fields)
        formValues.sellingWithMortgage = infoData.sellingWithMortgage || false;
        formValues.arrangingIndemnityPolicy = infoData.arrangingIndemnityPolicy || false;
        formValues.unregisteredProperty = infoData.unregisteredProperty || false;
        formValues.sharedOwnership = infoData.sharedOwnership || false;
        formValues.auctionRepossession = infoData.auctionRepossession || false;

        console.log('✅ Sale info prepared');

        // Populate Purchase Info section checkboxes
        console.log('☑️ === POPULATING PURCHASE INFO CHECKBOXES ===');
        // Use same top-level info object (all checkboxes are in one place per schema)

        // Purchase-specific info fields
        formValues.firstTimeBuyer = infoData.firstTimeBuyer || false;
        formValues.helpToBuyISA = infoData.helpToBuyISA || false;
        formValues.helpToBuy = infoData.helpToBuy || false;
        formValues.helpToBuyEquityLoan = infoData.helpToBuyEquityLoan || false;
        formValues.rightToBuy = infoData.rightToBuy || false;
        formValues.islamicMortgage = infoData.islamicMortgage || false;
        formValues.buyToLet = infoData.buyToLet || false;
        formValues.isNonUKResident = infoData.isNonUKResident || '';
        formValues.buyingWithMortgage = infoData.buyingWithMortgage || false;
        formValues.arrangingRedemption = infoData.arrangingRedemption || false;
        formValues.newBuild = infoData.newBuild || false;
        formValues.giftedDeposit = infoData.giftedDeposit || '';
        formValues.multipleProperties = infoData.multipleProperties || '';
        formValues.staircasing = infoData.staircasing || '';
        formValues.nonResidentialMixed = infoData.nonResidentialMixed || false;

        console.log('✅ Purchase info prepared');

        // Lender Panel
        const lenderPanelId = lead.lender_panel_id
          ? (typeof lead.lender_panel_id === 'object' ? lead.lender_panel_id._id : lead.lender_panel_id)
          : '';
        console.log('🏦 Lender Panel ID:', lenderPanelId);
        formValues.lender_panel_id = String(lenderPanelId);

        // Partner
        const partnerId = lead.partner_id
          ? (typeof lead.partner_id === 'object' ? lead.partner_id._id : lead.partner_id)
          : '';
        console.log('🤝 Partner ID:', partnerId);
        formValues.partner_id = String(partnerId);

        // Use setValue for each field to ensure they update correctly
        console.log('🔄 === SETTING FORM VALUES ===');
        console.log('Form values being set:', formValues);
        console.log('Number of fields:', Object.keys(formValues).length);

        // Set Sale Property Address fields
        console.log('Setting saleLine1:', formValues.saleLine1);
        setValue('saleLine1', formValues.saleLine1);
        console.log('Setting saleLine2:', formValues.saleLine2);
        setValue('saleLine2', formValues.saleLine2);
        console.log('Setting saleTown:', formValues.saleTown);
        setValue('saleTown', formValues.saleTown);
        console.log('Setting saleCounty:', formValues.saleCounty);
        setValue('saleCounty', formValues.saleCounty);
        console.log('Setting salePostcode:', formValues.salePostcode);
        setValue('salePostcode', formValues.salePostcode);
        console.log('Setting salePrice:', formValues.salePrice);
        setValue('salePrice', formValues.salePrice);
        console.log('Setting saleOwnershipType:', formValues.saleOwnershipType);
        setValue('saleOwnershipType', formValues.saleOwnershipType);
        console.log('✅ Sale property fields set');

        // Set Purchase Property Address fields
        console.log('Setting purchaseLine1:', formValues.purchaseLine1);
        setValue('purchaseLine1', formValues.purchaseLine1);
        console.log('Setting purchaseLine2:', formValues.purchaseLine2);
        setValue('purchaseLine2', formValues.purchaseLine2);
        console.log('Setting purchaseTown:', formValues.purchaseTown);
        setValue('purchaseTown', formValues.purchaseTown);
        console.log('Setting purchaseCounty:', formValues.purchaseCounty);
        setValue('purchaseCounty', formValues.purchaseCounty);
        console.log('Setting purchasePostcode:', formValues.purchasePostcode);
        setValue('purchasePostcode', formValues.purchasePostcode);
        console.log('Setting purchasePrice:', formValues.purchasePrice);
        setValue('purchasePrice', formValues.purchasePrice);
        console.log('Setting purchaseOwnershipType:', formValues.purchaseOwnershipType);
        setValue('purchaseOwnershipType', formValues.purchaseOwnershipType);
        console.log('Setting purchaseCompletionDate:', formValues.purchaseCompletionDate);
        setValue('purchaseCompletionDate', formValues.purchaseCompletionDate);
        console.log('✅ Purchase property fields set');

        // Set Sale Info checkboxes
        setValue('sellingWithMortgage', formValues.sellingWithMortgage);
        setValue('arrangingIndemnityPolicy', formValues.arrangingIndemnityPolicy);
        setValue('unregisteredProperty', formValues.unregisteredProperty);
        setValue('sharedOwnership', formValues.sharedOwnership);
        setValue('auctionRepossession', formValues.auctionRepossession);
        console.log('✅ Sale info fields set');

        // Set Purchase Info checkboxes and other info fields
        setValue('firstTimeBuyer', formValues.firstTimeBuyer);
        setValue('helpToBuyISA', formValues.helpToBuyISA);
        setValue('helpToBuy', formValues.helpToBuy);
        setValue('helpToBuyEquityLoan', formValues.helpToBuyEquityLoan);
        setValue('rightToBuy', formValues.rightToBuy);
        setValue('islamicMortgage', formValues.islamicMortgage);
        setValue('buyToLet', formValues.buyToLet);
        setValue('isNonUKResident', formValues.isNonUKResident);
        setValue('buyingWithMortgage', formValues.buyingWithMortgage || false);
        setValue('arrangingRedemption', formValues.arrangingRedemption || false);
        setValue('newBuild', formValues.newBuild || false);
        setValue('giftedDeposit', formValues.giftedDeposit || '');
        setValue('multipleProperties', formValues.multipleProperties || '');
        setValue('staircasing', formValues.staircasing || '');
        setValue('nonResidentialMixed', formValues.nonResidentialMixed || false);
        console.log('✅ Purchase info fields set');

        // Set Lender Panel and Partner
        setValue('lender_panel_id', formValues.lender_panel_id);
        setValue('partner_id', formValues.partner_id);
        console.log('✅ Lender panel and partner fields set');

        console.log('✅ === AUTO-FILL COMPLETE ===');
      } else {
        console.log('⚠️ No data returned from API');
      }
    } catch (error) {
      console.error('❌ Error fetching conveyancing details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial customer from URL if customerId is provided (same as Purchase form)
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      if (customerIdFromUrl && authCtx.token && sellers.length === 0 && purchasers.length === 0) {
        console.log('🔍 Fetching initial customer from URL:', customerIdFromUrl);
        try {
          // Use same endpoint as other components (supports both MongoDB ID and custom ID)
          const customerRes = await postRequest(`private/getCustomerIndividualData/${customerIdFromUrl}`, {}, authCtx.token);
          if (customerRes?.data) {
            console.log('✅ Fetched initial customer:', customerRes.data);
            // Set customer to both sellers and purchasers for Sale & Purchase
            setSellers([customerRes.data]);
            setPurchasers([customerRes.data]);
          }
        } catch (err) {
          console.error('❌ Error fetching initial customer:', err);
        }
      }
    };

    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token]);

  useEffect(() => {
    if (id && authCtx.token) {
      fetchConveyancingDetails();
    }
  }, [id, authCtx.token]);

  // Check sale postcode in branches when sale postcode changes
  useEffect(() => {
    const checkSalePostcode = async () => {
      if (!salePostcodeValue || salePostcodeValue.trim().length < 3) {
        setSalePostcodeMatch(null);
        setAvailableSaleBranches([]);
        setSelectedSaleBranch(null);
        setIsSalePostcodeValid(false);
        return;
      }

      try {
        setCheckingSalePostcode(true);

        // Clear previous states before starting validation
        setSalePostcodeMatch(null);
        setAvailableSaleBranches([]);
        setSelectedSaleBranch(null);
        setIsSalePostcodeValid(false);

        // STEP 1: Check if sale postcode exists via checkPostcodeInBranch API
        console.log('🔍 Step 1: Checking if sale postcode exists');
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: salePostcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 1 Result (Sale):', checkRes);

        // Check if Step 1 failed (invalid postcode)
        if (checkRes?.status === 404 || checkRes?.data?.exists === false) {
          // ❌ Step 1 Failed: Sale postcode does not exist
          console.log('❌ Step 1 Failed: Sale postcode does not exist');
          setSalePostcodeMatch(null);
          setAvailableSaleBranches([]);
          setSelectedSaleBranch(null);
          setIsSalePostcodeValid(false);

          // Show error toast for sale postcode with API message
          Swal.fire({
            title: 'Invalid Sale Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database. Please enter a valid UK postcode.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return; // Stop here, don't proceed to Step 2
        }

        // ✅ Step 1 Passed: Sale postcode exists
        console.log('✅ Step 1 Passed: Sale postcode exists');
        console.log('🔍 Step 2: Matching branch by sale postcode prefix');

        // STEP 2: Match branch by postcode prefix (only runs if Step 1 passed)
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: salePostcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 2 Result (Sale):', matchRes);

        if (matchRes?.data?.matched) {
          // ✅ Step 2 Passed: Branch matched
          console.log('✅ Step 2 Passed: Sale branch matched');
          setSalePostcodeMatch(matchRes.data.branch);
          setAvailableSaleBranches([]);
          setSelectedSaleBranch(null);
          setIsSalePostcodeValid(true);
        } else {
          // ❌ Step 2 Failed: Valid postcode but no branch serves this area
          console.log('❌ Step 2 Failed: No branch serves this sale postcode area');
          setSalePostcodeMatch(null);
          setAvailableSaleBranches([]);
          setSelectedSaleBranch(null);
          setIsSalePostcodeValid(false);

          Swal.fire({
            title: 'No Branch Coverage (Sale)',
            text: 'This sale postcode area is not currently served by any of our branches. Please contact support.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        console.error('Error checking sale postcode:', error);
        setIsSalePostcodeValid(false);
      } finally {
        setCheckingSalePostcode(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(checkSalePostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [salePostcodeValue, authCtx.token]);

  // Check purchase postcode in branches when purchase postcode changes
  useEffect(() => {
    const checkPurchasePostcode = async () => {
      if (!purchasePostcodeValue || purchasePostcodeValue.trim().length < 3) {
        setPurchasePostcodeMatch(null);
        setAvailablePurchaseBranches([]);
        setSelectedPurchaseBranch(null);
        setIsPurchasePostcodeValid(false);
        return;
      }

      try {
        setCheckingPurchasePostcode(true);

        // Clear previous states before starting validation
        setPurchasePostcodeMatch(null);
        setAvailablePurchaseBranches([]);
        setSelectedPurchaseBranch(null);
        setIsPurchasePostcodeValid(false);

        // STEP 1: Check if purchase postcode exists via checkPostcodeInBranch API
        console.log('🔍 Step 1: Checking if purchase postcode exists');
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: purchasePostcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 1 Result (Purchase):', checkRes);

        // Check if Step 1 failed (invalid postcode)
        if (checkRes?.status === 404 || checkRes?.data?.exists === false) {
          // ❌ Step 1 Failed: Purchase postcode does not exist
          console.log('❌ Step 1 Failed: Purchase postcode does not exist');
          setPurchasePostcodeMatch(null);
          setAvailablePurchaseBranches([]);
          setSelectedPurchaseBranch(null);
          setIsPurchasePostcodeValid(false);

          // Show error toast for purchase postcode with API message
          Swal.fire({
            title: 'Invalid Purchase Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database. Please enter a valid UK postcode.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return; // Stop here, don't proceed to Step 2
        }

        // ✅ Step 1 Passed: Purchase postcode exists
        console.log('✅ Step 1 Passed: Purchase postcode exists');
        console.log('🔍 Step 2: Matching branch by purchase postcode prefix');

        // STEP 2: Match branch by postcode prefix (only runs if Step 1 passed)
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: purchasePostcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 2 Result (Purchase):', matchRes);

        if (matchRes?.data?.matched) {
          // ✅ Step 2 Passed: Branch matched
          console.log('✅ Step 2 Passed: Purchase branch matched');
          setPurchasePostcodeMatch(matchRes.data.branch);
          setAvailablePurchaseBranches([]);
          setSelectedPurchaseBranch(null);
          setIsPurchasePostcodeValid(true);
        } else {
          // ❌ Step 2 Failed: Valid postcode but no branch serves this area
          console.log('❌ Step 2 Failed: No branch serves this purchase postcode area');
          setPurchasePostcodeMatch(null);
          setAvailablePurchaseBranches([]);
          setSelectedPurchaseBranch(null);
          setIsPurchasePostcodeValid(false);

          Swal.fire({
            title: 'No Branch Coverage (Purchase)',
            text: 'This purchase postcode area is not currently served by any of our branches. Please contact support.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        console.error('Error checking purchase postcode:', error);
        setIsPurchasePostcodeValid(false);
      } finally {
        setCheckingPurchasePostcode(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(checkPurchasePostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [purchasePostcodeValue, authCtx.token]);

  // Fetch active lender panels on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  const handleAddSeller = (customer) => {
    if (!sellers.some(s => s._id === customer._id)) {
      setSellers([...sellers, customer]);
    }
  };

  const handleRemoveSeller = (customerId) => {
    setSellers(sellers.filter(s => s._id !== customerId));
  };

  const handleAddPurchaser = (customer) => {
    if (!purchasers.some(p => p._id === customer._id)) {
      setPurchasers([...purchasers, customer]);
    }
  };

  const handleRemovePurchaser = (customerId) => {
    setPurchasers(purchasers.filter(p => p._id !== customerId));
  };

  const onSubmit = async (formData) => {
    if (sellers.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one seller'
      });
      return;
    }

    if (purchasers.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one purchaser'
      });
      return;
    }

    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingSalePostcode || checkingPurchasePostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isSalePostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Sale Postcode',
        text: 'Please enter a valid UK sale postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    if (!isPurchasePostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Purchase Postcode',
        text: 'Please enter a valid UK purchase postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    try {
      console.log('💾 === SUBMITTING SALE & PURCHASE ===');
      console.log('📍 Conveyancing ID:', id);
      console.log('👥 Sellers array:', sellers);
      console.log('👥 Purchasers array:', purchasers);
      console.log('👥 Sellers IDs:', sellers.map(s => s._id));
      console.log('👥 Purchasers IDs:', purchasers.map(p => p._id));

      const payload = {
        // Data from Step 1 (SimpleConveyancingForm)
        override_date: basicFormData.override_date || new Date(),
        group_code: basicFormData.group_code,
        acquisition: basicFormData.acquisition || 'Direct',
        product_id: basicFormData.product_id,
        service_type: basicFormData.service_type || 'Purchase_Sale',

        vendor_ids: sellers.map(s => s._id),
        purchaser_ids: purchasers.map(p => p._id),

        // Sale property
        sale_property_address: {
          address1: formData.saleLine1,
          address2: formData.saleLine2,
          town: formData.saleTown,
          country: formData.saleCounty,
          postcode: formData.salePostcode
        },
        sale_property_value: formData.salePrice,
        sale_ownership_type: formData.saleOwnershipType,

        // Purchase property
        purchase_property_address: {
          address1: formData.purchaseLine1,
          address2: formData.purchaseLine2,
          town: formData.purchaseTown,
          country: formData.purchaseCounty,
          postcode: formData.purchasePostcode
        },
        purchase_property_value: formData.purchasePrice,
        purchase_ownership_type: formData.purchaseOwnershipType,
        purchase_completion_date: formData.purchaseCompletionDate,

        lender_panel_id: formData.lender_panel_id || null,
        partner_id: formData.partner_id || null,

        // Sale info checkboxes
        selling_with_mortgage: formData.sellingWithMortgage,
        sale_arranging_indemnity_policy: formData.arrangingIndemnityPolicy,
        sale_unregistered_property: formData.unregisteredProperty,
        sale_shared_ownership: formData.sharedOwnership,
        sale_auction_repossession: formData.auctionRepossession,

        // Purchase info checkboxes
        first_time_buyer: formData.firstTimeBuyer,
        help_to_buy_isa: formData.helpToBuyISA,
        help_to_buy: formData.helpToBuy,
        help_to_buy_equity_loan: formData.helpToBuyEquityLoan,
        right_to_buy: formData.rightToBuy,
        purchase_shared_ownership: formData.sharedOwnership,
        buying_with_mortgage: formData.buyingWithMortgage,
        new_build: formData.newBuild,
        islamic_mortgage: formData.islamicMortgage,
        purchase_auction_repossession: formData.auctionRepossession,
        gifted_deposit: formData.giftedDeposit,
        multiple_properties: formData.multipleProperties,
        buy_to_let: formData.buyToLet,
        purchase_arranging_indemnity_policy: formData.arrangingIndemnityPolicy,
        purchase_unregistered_property: formData.unregisteredProperty,
        staircasing: formData.staircasing,
        is_non_uk_resident: formData.isNonUKResident,
        non_residential_mixed: formData.nonResidentialMixed
      };

      console.log('📦 Complete payload being sent:', payload);

      const actualLeadId = id === 'new' ? 'new' : id;
      const res = await postRequest(
        `private/saveConveyancingSalePurchaseToLead/${actualLeadId}`,
        payload,
        authCtx.token
      );
      console.log('📡 API Response:', res);
      
      if (res?.status === 'success' || res?.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: id === 'new'
            ? 'Sale & Purchase created and email sent successfully!'
            : 'Sale & Purchase details updated successfully!'
        }).then(() => {
          navigate('/leads');
        });
      } else {
        throw new Error(res?.message || 'Failed to save details');
      }
    } catch (error) {
      console.error('Error saving details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save details'
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            Edit Conveyancing #{conveyancingData?.reference || id} - Sale & Purchase
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Sale Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Sale Details
              </Typography>
              
              {/* Sale Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                  <Controller
                    name="saleLine1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.saleLine1}
                        helperText={errors.saleLine1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="saleLine2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="saleTown"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.saleTown}
                        helperText={errors.saleTown?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="saleCounty"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="salePostcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode"
                        variant="outlined"
                        size="small"
                        error={!!errors.salePostcode}
                        helperText={errors.salePostcode?.message}
                      />
                    )}
                  />

                  {/* Sale Postcode checking indicator */}
                  {checkingSalePostcode && salePostcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Sale Postcode match found */}
                  {salePostcodeMatch && !checkingSalePostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{salePostcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Sale Branch selection dropdown when postcode not found */}
                  {!salePostcodeMatch && availableSaleBranches.length > 0 && !checkingSalePostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedSaleBranch || ''}
                          onChange={(e) => {
                            const branchId = e.target.value;
                            setSelectedSaleBranch(branchId);

                            // When user manually selects a branch, mark sale postcode as valid
                            if (branchId) {
                              setIsSalePostcodeValid(true);
                            }

                            // Find the selected branch and auto-fill postcode
                            const branch = availableSaleBranches.find(b => b.id === branchId);
                            if (branch && branch.address.postcode) {
                              setValue('salePostcode', branch.address.postcode);
                            }
                          }}
                          label="Select Branch"
                        >
                          {availableSaleBranches.map((branch) => (
                            <MenuItem key={branch.id} value={branch.id}>
                              {branch.name} - {branch.address.postcode || 'No postcode'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Sale Price</Typography>
                      <Controller
                        name="salePrice"
                        control={control}
                        rules={{ required: 'Sale price is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }}
                            error={!!errors.salePrice}
                            helperText={errors.salePrice?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Ownership Type</Typography>
                      <Controller
                        name="saleOwnershipType"
                        control={control}
                        rules={{ required: 'Ownership type is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth size="small" error={!!errors.saleOwnershipType}>
                            <Select {...field}>
                              <MenuItem value="">Select Ownership Type</MenuItem>
                              {ownershipTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.saleOwnershipType && (
                              <Typography variant="caption" color="error">
                                {errors.saleOwnershipType.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Sellers Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Sellers
              </Typography>

              <Box sx={{ mb: 3 }}>
                {sellers.map((seller) => (
                  <Box
                    key={seller._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {seller.title} {seller.forename} {seller.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {seller.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {seller.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemoveSeller(seller._id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowSellerModal(true)}
                  sx={{ color: '#6f42c1', textTransform: 'none', mt: 1 }}
                >
                  ADD SELLER
                </Button>
              </Box>
              
              {/* Sale Info Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Sale Info
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {[
                  { name: 'sellingWithMortgage', label: 'Selling with Mortgage' },
                  { name: 'arrangingIndemnityPolicy', label: 'Arranging an Indemnity Policy' },
                  { name: 'unregisteredProperty', label: 'Unregistered Property' },
                  { name: 'sharedOwnership', label: 'Shared Ownership' },
                  { name: 'auctionRepossession', label: 'Auction / Repossession' }
                ].map((item) => (
                  <Controller
                    key={item.name}
                    name={item.name}
                    control={control}
                    render={({ field }) => (
                      <Button
                        variant="outlined"
                        onClick={() => field.onChange(!field.value)}
                        sx={{
                          borderColor: field.value ? '#6f42c1' : '#ced4da',
                          backgroundColor: field.value ? '#6f42c1' : 'white',
                          color: field.value ? 'white' : '#495057',
                          textTransform: 'none',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                          px: 2,
                          py: 0.75,
                          '&:hover': {
                            backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                            borderColor: field.value ? '#5a32a3' : '#adb5bd'
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                    )}
                  />
                ))}
              </Box>
            </Box>

            {/* Purchase Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Purchase Details
              </Typography>
              
              {/* Purchase Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                  <Controller
                    name="purchaseLine1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.purchaseLine1}
                        helperText={errors.purchaseLine1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="purchaseLine2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="purchaseTown"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.purchaseTown}
                        helperText={errors.purchaseTown?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="purchaseCounty"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="purchasePostcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode"
                        variant="outlined"
                        size="small"
                        error={!!errors.purchasePostcode}
                        helperText={errors.purchasePostcode?.message}
                      />
                    )}
                  />

                  {/* Purchase Postcode checking indicator */}
                  {checkingPurchasePostcode && purchasePostcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Purchase Postcode match found */}
                  {purchasePostcodeMatch && !checkingPurchasePostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{purchasePostcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Purchase Branch selection dropdown when postcode not found */}
                  {!purchasePostcodeMatch && availablePurchaseBranches.length > 0 && !checkingPurchasePostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedPurchaseBranch || ''}
                          onChange={(e) => {
                            const branchId = e.target.value;
                            setSelectedPurchaseBranch(branchId);

                            // When user manually selects a branch, mark purchase postcode as valid
                            if (branchId) {
                              setIsPurchasePostcodeValid(true);
                            }

                            // Find the selected branch and auto-fill postcode
                            const branch = availablePurchaseBranches.find(b => b.id === branchId);
                            if (branch && branch.address.postcode) {
                              setValue('purchasePostcode', branch.address.postcode);
                            }
                          }}
                          label="Select Branch"
                        >
                          {availablePurchaseBranches.map((branch) => (
                            <MenuItem key={branch.id} value={branch.id}>
                              {branch.name} - {branch.address.postcode || 'No postcode'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Purchase Price</Typography>
                      <Controller
                        name="purchasePrice"
                        control={control}
                        rules={{ required: 'Purchase price is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }}
                            error={!!errors.purchasePrice}
                            helperText={errors.purchasePrice?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Ownership Type</Typography>
                      <Controller
                        name="purchaseOwnershipType"
                        control={control}
                        rules={{ required: 'Ownership type is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth size="small" error={!!errors.purchaseOwnershipType}>
                            <Select {...field}>
                              <MenuItem value="">Select Ownership Type</MenuItem>
                              {ownershipTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.purchaseOwnershipType && (
                              <Typography variant="caption" color="error">
                                {errors.purchaseOwnershipType.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Est. Completion Date</Typography>
                      <Controller
                        name="purchaseCompletionDate"
                        control={control}
                        rules={{ required: 'Completion date is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="date"
                            size="small"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            error={!!errors.purchaseCompletionDate}
                            helperText={errors.purchaseCompletionDate?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Purchasers Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Purchasers
              </Typography>

              <Box sx={{ mb: 3 }}>
                {purchasers.map((purchaser) => (
                  <Box
                    key={purchaser._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {purchaser.title} {purchaser.forename} {purchaser.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {purchaser.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {purchaser.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemovePurchaser(purchaser._id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowPurchaserModal(true)}
                  sx={{ color: '#6f42c1', textTransform: 'none', mt: 1 }}
                >
                  ADD PURCHASER
                </Button>
              </Box>

              {/* Lender Panel Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Lender Panel
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="lender_panel_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          sx={{ backgroundColor: 'white' }}
                        >
                          <MenuItem value="">
                            <em>Select Lender Panel (Optional)</em>
                          </MenuItem>
                          {lenderPanels.map((panel) => (
                            <MenuItem key={panel._id} value={panel._id}>
                              {panel.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Partner Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Partner
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="partner_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          sx={{ backgroundColor: 'white' }}
                        >
                          <MenuItem value="">
                            <em>Select Partner (Optional)</em>
                          </MenuItem>
                          {partners.map((partner) => (
                            <MenuItem key={partner._id} value={partner._id}>
                              {partner.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Purchase Info Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Purchase Info
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { name: 'firstTimeBuyer', label: 'First Time Buyer' },
                  { name: 'helpToBuyISA', label: 'Help to Buy ISA' },
                  { name: 'helpToBuy', label: 'Help to Buy' },
                  { name: 'helpToBuyEquityLoan', label: 'Help to Buy Equity Loan' },
                  { name: 'rightToBuy', label: 'Right to Buy' },
                  { name: 'buyingWithMortgage', label: 'Buying with Mortgage' },
                  { name: 'newBuild', label: 'New Build' },
                  { name: 'islamicMortgage', label: 'Islamic Mortgage' },
                  { name: 'buyToLet', label: 'Buy to Let' },
                  { name: 'nonResidentialMixed', label: 'Non-Residential / Mixed Use' }
                ].map((item) => (
                  <Controller
                    key={item.name}
                    name={item.name}
                    control={control}
                    render={({ field }) => (
                      <Button
                        variant="outlined"
                        onClick={() => field.onChange(!field.value)}
                        sx={{
                          borderColor: field.value ? '#6f42c1' : '#ced4da',
                          backgroundColor: field.value ? '#6f42c1' : 'white',
                          color: field.value ? 'white' : '#495057',
                          textTransform: 'none',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                          px: 2,
                          py: 0.75,
                          '&:hover': {
                            backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                            borderColor: field.value ? '#5a32a3' : '#adb5bd'
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                    )}
                  />
                ))}
                
                {/* Gifted Deposit with input */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Controller
                    name="giftedDeposit"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => field.onChange(field.value ? '' : '1')}
                          sx={{
                            borderColor: field.value ? '#6f42c1' : '#ced4da',
                            backgroundColor: field.value ? '#6f42c1' : 'white',
                            color: field.value ? 'white' : '#495057',
                            textTransform: 'none',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            px: 2,
                            py: 0.75,
                            '&:hover': {
                              backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                              borderColor: field.value ? '#5a32a3' : '#adb5bd'
                            }
                          }}
                        >
                          Gifted Deposit - Num Giftors
                        </Button>
                        {field.value && (
                          <TextField
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            size="small"
                            sx={{ 
                              width: '60px', 
                              ml: 1,
                              '& .MuiOutlinedInput-root': {
                                height: '34px'
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  />
                </Box>
                
                {/* Multiple Properties with input */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Controller
                    name="multipleProperties"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => field.onChange(field.value ? '' : '1')}
                          sx={{
                            borderColor: field.value ? '#6f42c1' : '#ced4da',
                            backgroundColor: field.value ? '#6f42c1' : 'white',
                            color: field.value ? 'white' : '#495057',
                            textTransform: 'none',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            px: 2,
                            py: 0.75,
                            '&:hover': {
                              backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                              borderColor: field.value ? '#5a32a3' : '#adb5bd'
                            }
                          }}
                        >
                          Multiple Properties - Num Properties
                        </Button>
                        {field.value && (
                          <TextField
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            size="small"
                            sx={{ 
                              width: '60px', 
                              ml: 1,
                              '& .MuiOutlinedInput-root': {
                                height: '34px'
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  />
                </Box>
                
                {/* Staircasing with input */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Controller
                    name="staircasing"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => field.onChange(field.value ? '' : '10')}
                          sx={{
                            borderColor: field.value ? '#6f42c1' : '#ced4da',
                            backgroundColor: field.value ? '#6f42c1' : 'white',
                            color: field.value ? 'white' : '#495057',
                            textTransform: 'none',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            px: 2,
                            py: 0.75,
                            '&:hover': {
                              backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                              borderColor: field.value ? '#5a32a3' : '#adb5bd'
                            }
                          }}
                        >
                          Staircasing - % Purchasing
                        </Button>
                        {field.value && (
                          <TextField
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            size="small"
                            sx={{ 
                              width: '70px', 
                              ml: 1,
                              '& .MuiOutlinedInput-root': {
                                height: '34px'
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  />
                </Box>
                
                {/* Is Non-UK Resident with input */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Controller
                    name="isNonUKResident"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => field.onChange(field.value ? '' : '1')}
                          sx={{
                            borderColor: field.value ? '#6f42c1' : '#ced4da',
                            backgroundColor: field.value ? '#6f42c1' : 'white',
                            color: field.value ? 'white' : '#495057',
                            textTransform: 'none',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            px: 2,
                            py: 0.75,
                            '&:hover': {
                              backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                              borderColor: field.value ? '#5a32a3' : '#adb5bd'
                            }
                          }}
                        >
                          Is Non-UK Resident - Num Non-Resident
                        </Button>
                        {field.value && (
                          <TextField
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            size="small"
                            sx={{ 
                              width: '60px', 
                              ml: 1,
                              '& .MuiOutlinedInput-root': {
                                height: '34px'
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  />
                </Box>
              </Box>
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={checkingSalePostcode || checkingPurchasePostcode || !isSalePostcodeValid || !isPurchasePostcodeValid}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Seller Search Modal */}
      <CustomerSearchModal
        show={showSellerModal}
        onHide={() => setShowSellerModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddSeller}
        selectedCustomers={sellers}
        title="Add Seller"
      />

      {/* Purchaser Search Modal */}
      <CustomerSearchModal
        show={showPurchaserModal}
        onHide={() => setShowPurchaserModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddPurchaser}
        selectedCustomers={purchasers}
        title="Add Purchaser"
      />
    </>
  );
}